// Digital products we build and host. Each product has its own page at
// /products/<slug>. Pricing is shared with the home page teaser.

export const products = [
  {
    slug: "patient-management",
    icon: "patient-management",
    theme: "blue",
    name: "Nursing One",
    shortName: "Nursing One",
    tagline: "Patient management & billing: records, appointments and invoices in one place.",
    lead:
      "Nursing One is a simple patient management and billing system for clinics and small hospitals: register patients, manage visits and bill without paperwork piling up.",
    highlights: [
      "Patient records & visit history",
      "Appointments & queue",
      "GST-ready invoices",
      "Daily collection reports",
    ],
    features: [
      {
        title: "Patient registration & records",
        text: "Register once, find in seconds. Contact details, history, notes and documents stay attached to the patient.",
      },
      {
        title: "Appointments & visit history",
        text: "Book, reschedule and track visits. Every consultation is logged against the patient automatically.",
      },
      {
        title: "Billing & invoices",
        text: "Create itemised, GST-ready invoices for consultations, procedures and medicines in a couple of clicks.",
      },
      {
        title: "Payments & dues",
        text: "Record cash, UPI and card payments, track partial payments and see outstanding dues at a glance.",
      },
      {
        title: "Reports that matter",
        text: "Daily collections, outstanding balances, visits per doctor and more, ready to export.",
      },
      {
        title: "Staff roles & access",
        text: "Reception, doctors and accounts each see what they need. Every change is tracked.",
      },
    ],
    benefits: [
      { title: "Faster front desk", text: "Less time searching files, more time with patients." },
      { title: "Nothing gets lost", text: "Records and bills live in one secure place, backed up daily." },
      { title: "Clear finances", text: "Know what was collected and what is due, every day." },
    ],
    audience: ["Clinics", "Dental practices", "Diagnostic centres", "Physiotherapy", "Small hospitals"],
    pricing: {
      label: "Starts at",
      price: "₹28,499",
      originalPrice: "₹47,999",
      note: "One-time setup for a single clinic. Hosting, backups and support are covered by a yearly plan quoted with your setup.",
    },
    seo: {
      title: "Nursing One — Patient Management & Billing Software for Clinics | Visible Online",
      description:
        "Nursing One is simple patient management and billing software for clinics and small hospitals: records, appointments, GST-ready invoices, payments and reports.",
    },
  },
  {
    slug: "dialysis-inventory",
    icon: "dialysis-inventory",
    theme: "teal",
    name: "Dialysis Inventory Management",
    shortName: "Dialysis inventory management",
    tagline: "Track consumables, stock levels and reorders.",
    lead:
      "Built for dialysis units. Know exactly what stock you have, what each session uses and when to reorder before you run short.",
    highlights: [
      "Dialyzers, bloodlines & concentrates",
      "Batch & expiry tracking",
      "Per-session consumption",
      "Low-stock alerts",
    ],
    features: [
      {
        title: "Item master for every consumable",
        text: "Dialyzers, bloodlines, concentrates, needles, heparin and more, with units, suppliers and reorder levels.",
      },
      {
        title: "Batch & expiry tracking",
        text: "Receive stock by batch, see what expires soon and issue the oldest stock first.",
      },
      {
        title: "Per-session consumption",
        text: "Log what each dialysis session uses. Stock updates itself, and every item traces back to a patient and date.",
      },
      {
        title: "Low-stock alerts & reorder points",
        text: "Set a minimum for each item and get alerted before you run out.",
      },
      {
        title: "Purchases & suppliers",
        text: "Record purchase orders and deliveries, and compare supplier prices over time.",
      },
      {
        title: "Usage & wastage reports",
        text: "Monthly consumption, cost per session, expired stock and variance reports for audits.",
      },
    ],
    benefits: [
      { title: "Never run short", text: "Alerts arrive before a session is at risk, not after." },
      { title: "Cut wastage", text: "First-expiry-first-out issuing keeps expired stock to a minimum." },
      { title: "Audit-ready", text: "Every unit is traceable from purchase to patient." },
    ],
    audience: ["Dialysis centres", "Nephrology units", "Hospitals with dialysis wards", "Charitable dialysis trusts"],
    pricing: {
      label: "Starts at",
      price: "₹28,499",
      originalPrice: "₹47,999",
      note: "One-time setup for a single unit. Hosting, backups and support are covered by a yearly plan quoted with your setup.",
    },
    seo: {
      title: "Dialysis Inventory Management Software | Visible Online",
      description:
        "Inventory software for dialysis units: track dialyzers, bloodlines and concentrates by batch and expiry, log per-session use and get low-stock alerts.",
    },
  },
  {
    slug: "pathology-lab",
    icon: "pathology-lab",
    theme: "purple",
    name: "PathLab One",
    shortName: "PathLab One",
    tagline: "Sample tracking, test reports and billing for diagnostic labs.",
    lead:
      "PathLab One is a complete pathology lab management system: register patients, track samples, generate test reports and collect payments without paper or confusion.",
    highlights: [
      "Sample & barcode tracking",
      "Test report generation",
      "GST-ready billing",
      "Doctor referral tracking",
    ],
    features: [
      {
        title: "Patient & sample registration",
        text: "Register walk-ins and referred patients, assign sample IDs and track every sample from collection to result.",
      },
      {
        title: "Test catalogue & order management",
        text: "Maintain a master list of tests with pricing, profiles and turnaround times. Add multiple tests in one order.",
      },
      {
        title: "Result entry & report generation",
        text: "Enter results against reference ranges, flag abnormal values and generate branded PDF reports ready to share.",
      },
      {
        title: "Billing & payments",
        text: "Create GST-ready invoices, record cash and UPI payments, and track pending dues by patient and date.",
      },
      {
        title: "Doctor referral management",
        text: "Track referring doctors, the tests they order and the commission owed, all in one place.",
      },
      {
        title: "Reports & analytics",
        text: "Daily collection summaries, test-wise revenue, turnaround-time analysis and outstanding dues — ready to export.",
      },
    ],
    benefits: [
      { title: "Faster report delivery", text: "Reports go out as soon as results are entered — no printing queues." },
      { title: "Error-free results", text: "Reference ranges flag abnormals automatically so nothing slips through." },
      { title: "Clear revenue picture", text: "Know what was collected and what is outstanding every day." },
    ],
    audience: ["Standalone pathology labs", "Hospital diagnostic departments", "Collection centres", "Multi-branch labs"],
    pricing: {
      label: "Starts at",
      price: "₹28,499",
      originalPrice: "₹47,999",
      note: "One-time setup for a single lab. Hosting, backups and support are covered by a yearly plan quoted with your setup.",
    },
    seo: {
      title: "PathLab One — Pathology Lab Management Software | Visible Online",
      description:
        "PathLab One is pathology lab management software: register patients, track samples, generate test reports, bill and track referrals in one place.",
    },
  },
  {
    slug: "school-management",
    icon: "school-management",
    theme: "amber",
    name: "School One",
    shortName: "School One",
    tagline: "Admissions, attendance, fees and academics — one system for your school.",
    lead:
      "School One brings every department together: admissions, attendance, fee collection, examination results and parent communication, on a single hosted platform your staff can use from day one.",
    highlights: [
      "Student admissions & profiles",
      "Attendance tracking",
      "Fee collection & receipts",
      "Marks & report cards",
    ],
    features: [
      {
        title: "Student admissions & profiles",
        text: "Manage enquiries, register students, store documents and track class allocation — all in one place.",
      },
      {
        title: "Attendance management",
        text: "Mark daily attendance class-by-class, flag habitual absentees and share reports with parents.",
      },
      {
        title: "Fee collection & accounting",
        text: "Set fee structures per class, collect and receipt payments, and track dues with automatic reminders.",
      },
      {
        title: "Examinations & results",
        text: "Schedule exams, enter marks, calculate grades and publish result cards without manual computation.",
      },
      {
        title: "Class & timetable management",
        text: "Assign subjects, teachers and rooms. Timetable changes update everywhere instantly.",
      },
      {
        title: "Notices & communication",
        text: "Publish notices for students and parents, and send targeted messages by class or section.",
      },
    ],
    benefits: [
      { title: "Less paperwork at every desk", text: "Admissions, attendance and fees all handled digitally from day one." },
      { title: "Fees collected on time", text: "Automated due tracking and receipts reduce follow-up calls." },
      { title: "Parents stay informed", text: "Results and notices reach parents the moment they are published." },
    ],
    audience: ["Schools", "Junior colleges", "Coaching institutes", "Tutorial centres"],
    pricing: {
      label: "Starts at",
      price: "₹28,499",
      originalPrice: "₹47,999",
      note: "One-time setup for a single institution. Hosting, backups and support are covered by a yearly plan quoted with your setup.",
    },
    seo: {
      title: "School One — School Management Software | Visible Online",
      description:
        "School One is school management software for admissions, attendance, fee collection, examinations and parent communication — all in one hosted system.",
    },
  },
  {
    slug: "pharmacy-management",
    icon: "pharmacy-management",
    theme: "green",
    name: "PharmOne",
    shortName: "PharmOne",
    tagline: "Stock, billing and expiry control for retail and hospital pharmacies.",
    lead:
      "PharmOne keeps your pharmacy running smoothly: manage medicines by batch and expiry, process prescriptions, generate GST-ready bills and track purchases and returns without confusion.",
    highlights: [
      "Medicine stock & expiry",
      "Prescription billing",
      "GST-ready invoices",
      "Purchase & supplier tracking",
    ],
    features: [
      {
        title: "Medicine master & stock management",
        text: "Maintain a full catalogue of medicines with generics, strengths, units and rack locations. Stock updates on every sale and purchase.",
      },
      {
        title: "Prescription billing",
        text: "Search by name or generic, auto-fill from prescriptions and generate itemised bills in seconds.",
      },
      {
        title: "Batch & expiry tracking",
        text: "Receive stock by batch and expiry date, issue the nearest-to-expiry first and get alerts before stock expires.",
      },
      {
        title: "Purchase & supplier management",
        text: "Record purchase orders and invoices, compare supplier prices and track credits and payments.",
      },
      {
        title: "Returns & adjustments",
        text: "Handle customer returns and supplier returns cleanly, with stock and account entries updated automatically.",
      },
      {
        title: "Reports & analytics",
        text: "Daily sales, purchase summaries, expiry alerts, slow-moving stock and GST reports — ready to export.",
      },
    ],
    benefits: [
      { title: "Never miss an expiry", text: "Alerts arrive weeks before stock expires so you can return or clear it." },
      { title: "Faster billing", text: "Generic search and prescription auto-fill cut counter time on every bill." },
      { title: "Complete purchase records", text: "Every batch is traceable from supplier invoice to patient sale." },
    ],
    audience: ["Retail pharmacies", "Hospital pharmacies", "Medical stores", "Multi-outlet chains"],
    pricing: {
      label: "Starts at",
      price: "₹28,499",
      originalPrice: "₹47,999",
      note: "One-time setup for a single outlet. Hosting, backups and support are covered by a yearly plan quoted with your setup.",
    },
    seo: {
      title: "PharmOne — Pharmacy Management Software | Visible Online",
      description:
        "PharmOne is pharmacy management software: manage medicine stock by batch and expiry, process prescriptions, generate GST bills and track purchases and returns.",
    },
  },
];

export const getProduct = (slug) => products.find((p) => p.slug === slug);
